/**
 * Plan-entitlement client (feature flags + countable plafonds) — L-entitlements.
 *
 * Distinct from `lib/api/entitlements.ts` (AI-capability governance): this reads
 * the tenant's SUBSCRIPTION plan (limits + boolean feature flags) off
 * `GET /api/v1/subscription/me`, which the backend now returns with `features`
 * and `upgrade_url`. Consumed by the shared `useEntitlement()` store/hook and
 * the `<PlanGate>` component (features/entitlements/**).
 */
import { getMySubscription } from '@/lib/api/subscription';

export interface PlanEntitlements {
  planCode: string;
  status: string;
  limits: Record<string, number>;
  features: Record<string, boolean>;
  upgradeUrl: string;
  /**
   * The caller is the Karexia platform superadmin (server flag
   * `is_superadmin`, decided from the Karexia client role): every feature is
   * unlocked and every limit is unlimited, whatever the tenant's plan.
   */
  superadmin?: boolean;
  /** The tenant plan's OWN feature flags (before the superadmin override). */
  planFeatures?: Record<string, boolean>;
}

const DEFAULT_UPGRADE_URL = '/settings/subscription';

/** Fetch the caller-tenant's plan limits + feature flags. */
export async function getPlanEntitlements(): Promise<PlanEntitlements> {
  const me = await getMySubscription();
  return {
    planCode: me.subscription.plan_code,
    status: me.subscription.status,
    limits: me.limits ?? me.plan?.limits ?? {},
    features: me.features ?? me.plan?.features ?? {},
    upgradeUrl: me.upgrade_url ?? DEFAULT_UPGRADE_URL,
    superadmin: me.is_superadmin === true,
    planFeatures: me.plan?.features ?? me.features ?? {},
  };
}

/** True only when the plan explicitly includes `key` (fail-closed flag). */
export function planHasFeature(ent: PlanEntitlements | null, key: string): boolean {
  if (ent?.superadmin) return true;
  return Boolean(ent?.features?.[key]);
}

/** The plafond for `key` (`-1` = unlimited), or null when uncapped. */
export function planLimitFor(ent: PlanEntitlements | null, key: string): number | null {
  if (ent?.superadmin) return -1;
  const v = ent?.limits?.[key];
  return typeof v === 'number' ? v : null;
}
